'use client';

import React, { useState, useRef, useEffect } from 'react';
import { observer } from 'mobx-react';
import sampleSelectorsModel from './SamplesSelectorModel';
import samplesEditorModel from '../SamplesEditor/SamplesEditorModel';
import samplesModel, { SAMPLES_MODE } from '../SamplesModel';
import { Sample } from '@/sharedTypes/Api';
import './SamplesSelector.css';
import samplesSelectorModel from './SamplesSelectorModel';
import { Input } from '@/components/ui/input';

import {
  getCategorizedSamples,
  isAdminPage,
  isDebugPage,
  rightLanguage,
} from '@/sharedTypes/helpers';
import { IconButton } from '@/components/ui/button';
import {
  PiHeartFill,
  PiHeartLight,
  PiMagnifyingGlass,
  PiPencilSimpleLight,
  PiPlusCircleLight,
} from 'react-icons/pi';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useTour } from '@reactour/tour';
import TooltipSamplesSelector from '@/components/Tooltips/TooltipSamplesSelector';
import TooltipFavoritesUnlogged from '@/components/Tooltips/TooltipFavoritesUnlogged';
import { FavoriteType } from '@/types/favorites';
import { useFavorites } from '@/hooks/useFavorites';
import { signIn, useSession } from 'next-auth/react';
const SamplesSelector = observer(() => {
  const {
    favoriteItems,
    favoritesLoaded,
    toggleSingleFavorite,
    handleGetFavorites,
    loadInitialFavorites,
    isFavorite,
  } = useFavorites();

  // This ref will point to the DOM div for the *currently selected* sample
  const selectedRowRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [categorizedSamples, setCategorizedSamples] = useState<{
    [category: string]: Sample[];
  }>({});
  const [areFavoritesShown, setAreFavoritesShown] = useState(false);
  const isUserLoggedIn = useSession().data?.accessToken;

  const { t } = useTranslation();

  useEffect(() => {
    const updateDisplayedSamples = async () => {
      let samplesToShow;

      if (searchQuery.length > 0) {
        samplesToShow = sampleSelectorsModel.applySearch(
          searchQuery,
          areFavoritesShown,
          favoriteItems
        );
      } else if (areFavoritesShown) {
        // just show favorites without search
        samplesToShow = await sampleSelectorsModel.selectFavorites(
          favoriteItems
        );
      } else {
        samplesToShow = sampleSelectorsModel.samples;
      }

      setCategorizedSamples(getCategorizedSamples(samplesToShow));
    };

    updateDisplayedSamples();
  }, [searchQuery, areFavoritesShown, favoriteItems]);

  useEffect(() => {
    // Scroll to the selected sample when it changes
    if (selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({
        behavior: 'auto',
        block: 'center',
      });
    }
  }, [samplesSelectorModel.selectedSample, categorizedSamples]);

  const { currentStep, setCurrentStep, isOpen: isTourOpen } = useTour();

  const handleSampleSelect = async (sample: Sample) => {
    console.log(
      '###Sample clicked, current step:',
      currentStep,
      'tour open:',
      isTourOpen
    );
    // Your existing sample selection logic
    await samplesSelectorModel.triggerSampleSelection(sample.identifier);

    // If tour is running and we're on step 1 (sample selection), advance to step 2
    if (isTourOpen && currentStep === 1) {
      setTimeout(() => {
        setCurrentStep(2); // Advance to practice mode step
      }, 300); // Small delay to allow UI to update
    }
  };

  useEffect(() => {
    loadInitialFavorites();
  }, []);

  // Remove focus on mobile devices when component mounts
  useEffect(() => {
    const isMobileDevice =
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    const isTouchDevice =
      'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isMobile = isMobileDevice || isTouchDevice;

    if (isMobile && inputRef.current) {
      // Use setTimeout to ensure this runs after Radix UI's focus management
      setTimeout(() => {
        inputRef.current?.blur();
      }, 0);
    }
  }, []);

  const toggleFavoritesView = async () => {
    setAreFavoritesShown(!areFavoritesShown);
    if (!areFavoritesShown) {
      await handleGetFavorites(FavoriteType.SAMPLE);
      setCategorizedSamples(
        getCategorizedSamples(
          await sampleSelectorsModel.selectFavorites(favoriteItems)
        )
      );
    } else {
      setCategorizedSamples(
        getCategorizedSamples(sampleSelectorsModel.samples)
      );
    }
  };

  return (
    <div className='flex flex-col rounded-md max-h-full overflow-hidden'>
      <div className='border-b bg-white'>
        <div className='flex items-center gap-6 py-2 px-2 md:py-3'>
          <div className='relative w-full'>
            <div className='absolute text-zinc-500 w-8 top-0 bottom-0 flex items-center justify-center'>
              <PiMagnifyingGlass size={16} />
            </div>
            <Input
              ref={inputRef}
              className='pl-8'
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              data-testid='samples_search_input'
              placeholder={t('samples.search')}
            />
          </div>
          <div className='flex items-center gap-1'>
            {
              <TooltipFavoritesUnlogged 
                toggleFavoritesView={toggleFavoritesView}
                areFavoritesShown={areFavoritesShown}
                isUserLoggedIn={isUserLoggedIn}
              />
            }
            {!(isAdminPage() || isDebugPage()) && (
              <div className='hidden md:block [@media(hover:hover)]:block [@media(pointer:fine)]:block'>
                <TooltipSamplesSelector />
              </div>
            )}
            {(isAdminPage() || isDebugPage()) && (
              <IconButton
                data-testid='sample_upload_btn'
                onClick={() => {
                  samplesModel.currentMode = SAMPLES_MODE.UPLOAD;
                }}
              >
                <PiPlusCircleLight size={28} />
              </IconButton>
            )}
          </div>
        </div>
      </div>
      {isAdminPage() && (
        <div>
          <button
            onClick={() => {
              samplesEditorModel.rebuildCaches();
            }}
          >
            Пересчитать кеш
          </button>
          <button
            onClick={() => {
              samplesEditorModel.rebuildCaches(true);
            }}
          >
            Пересчитать кеш только миди
          </button>
        </div>
      )}
      {Object.keys(categorizedSamples).length === 0 && (
        <>
          <div className='text-sm text-zinc-500 text-center py-2'>
            {areFavoritesShown
              ? 'Нет избранных произведений'
              : 'Нет произведений'}
          </div>
        </>
      )}
      <div className='overflow-auto flex-1 min-h-1/2'>
        {Object.keys(categorizedSamples)
          .sort()
          .map((category, index: number) => (
            <div key={index}>
              <Separator />
              <div className='py-1 px-1 md:py-2'>
                <div className='text-sm md:text-base font-semibold py-1 px-2 md:py-2'>
                  {category}
                </div>
                {categorizedSamples[category]
                  .sort((a: Sample, b: Sample) => {
                    const aTitle = rightLanguage(a.metadata.title) || '';
                    const bTitle = rightLanguage(b.metadata.title) || '';
                    // Collator configured for Russian + English, numeric order, ignoring case distinctions:
                    const collator = new Intl.Collator(['ru', 'en'], {
                      numeric: true,
                      sensitivity: 'base',
                    });
                    return collator.compare(aTitle, bTitle);
                  })
                  .map((sample: Sample) => {
                    let midiInfo = '';
                    let MColor = 'green';
                    if (sample.metadata.midiLoaded) {
                      midiInfo = `M ${sample.metadata.midiWrongNotesCount}`;
                      if (sample.metadata.midiWrongNotesCount) {
                        const wrongCount = sample.metadata.midiWrongNotesCount;
                        if (wrongCount > 0 && wrongCount < 10) {
                          MColor = 'orange';
                        } else if (wrongCount > 10) {
                          MColor = 'red';
                        }
                      }
                    }
                    const midiStyle = { color: MColor };
                    const isSelected =
                      sample.identifier ===
                      samplesSelectorModel.selectedSample?.identifier;
                    return (
                      <div
                        key={sample.identifier}
                        ref={isSelected ? selectedRowRef : null}
                        className={cn(
                          'px-2 py-1 min-h-8 md:py-1.5 md:min-h-10 flex gap-4 rounded-md cursor-pointer justify-between w-full group',
                          isSelected ? 'bg-zinc-100' : 'hover:bg-zinc-100'
                        )}
                        data-testid={`samples_selector_${rightLanguage(
                          sample.metadata.title
                        )}`}
                        onClick={() => handleSampleSelect(sample)}
                      >
                        <div className='flex flex-1 shrink items-center min-w-0 gap-2'>
                          <div className='min-w-0  shrink'>
                            <p
                              className='text-sm truncate min-w-0'
                              title={rightLanguage(sample?.metadata?.title)}
                            >
                              {/* {rightLanguage(sample?.metadata?.author)}:{' '} */}
                              {rightLanguage(sample?.metadata?.title)}
                            </p>
                          </div>
                          {isAdminPage() && (
                            <span
                              className='whitespace-nowrap'
                              style={midiStyle}
                            >
                              {midiInfo}
                            </span>
                          )}
                        </div>
                        <div className='shrink-0 flex items-center'>
                          <button
                            onClick={(
                              e: React.MouseEvent<HTMLButtonElement>
                            ) => {
                              e.stopPropagation();
                              isUserLoggedIn
                                ? toggleSingleFavorite(
                                    sample.identifier,
                                    FavoriteType.SAMPLE
                                  )
                                : signIn('keycloak');
                            }}
                            className={
                              cn(
                                'p-1 cursor-pointer rounded-md hover:text-accent-blue transition-all',
                                favoritesLoaded && isFavorite(sample.identifier)
                                  ? 'opacity-100'
                                  : 'opacity-0 group-hover:opacity-100'
                              )
                            }
                            data-testid={`samples_selector_favorite_btn__${rightLanguage(sample.metadata.title)}`}
                          >
                            {favoritesLoaded &&
                            isFavorite(sample.identifier) ? (
                              <PiHeartFill size={20} color='#0c8ce9' />
                            ) : (
                              <PiHeartLight size={20} />
                            )}
                          </button>
                          {(isAdminPage() || isDebugPage()) && (
                            <IconButton
                              data-testid={
                                'samples_selector_edit_btn__' +
                                rightLanguage(sample.metadata.title) +
                                '_' +
                                rightLanguage(sample.metadata.author)
                              }
                              onClick={async (e) => {
                                e.stopPropagation();
                                await samplesEditorModel.selectSample(sample);
                                samplesModel.currentMode = SAMPLES_MODE.EDIT;
                              }}
                              className='transition-all opacity-0 group-hover:opacity-100'
                            >
                              <PiPencilSimpleLight size={20} />
                            </IconButton>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
});

export default SamplesSelector;
